// src/ReceiveParts.js
import { useEffect, useState } from "react";
import { apiFetch } from "./api";
import QRScannerModal from "./QRScannerModal";
import BarcodePrintModal from "./BarcodePrintModal"; // ✅ ADD THIS IMPORT

export default function ReceiveParts({ site }) {
  const [searchBy, setSearchBy] = useState("Part Number");
  const [term, setTerm] = useState("");
  const [partInfo, setPartInfo] = useState(null); // {part, po_required, uom_display, cost_display, received_uom, unit_uom...}
  const [err, setErr] = useState("");
  const [scanOpen, setScanOpen] = useState(false);

  const [poOptions, setPoOptions] = useState(["(None)"]);
  const [po, setPo] = useState("(None)");

  const [qtyReceived, setQtyReceived] = useState("");
  const [qtyPerUom, setQtyPerUom] = useState("");

  // Advance
  const [shippingCost, setShippingCost] = useState("");
  const [shippingType, setShippingType] = useState("");
  const [expiration, setExpiration] = useState("");
  const [shippingTypes, setShippingTypes] = useState(["(None)"]);

  const [saving, setSaving] = useState(false);

  // ✅ PRINT MODAL STATE (ADD THESE)
  const [printOpen, setPrintOpen] = useState(false);
  const [printPart, setPrintPart] = useState(null);

  // Load PO list (select only)
  useEffect(() => {
    if (!site) return;
    apiFetch(`/api/po/options?site=${encodeURIComponent(site)}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => setPoOptions(["(None)", ...(list || [])]))
      .catch(() => setPoOptions(["(None)"]));
  }, [site]);

  // Load shipping types dropdown
  useEffect(() => {
    if (!site) return;
    apiFetch(`/api/shipping/types?site=${encodeURIComponent(site)}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => setShippingTypes(["(None)", ...(list || [])]))
      .catch(() => setShippingTypes(["(None)"]));
  }, [site]);

  const loadPart = async (value) => {
    const t = (value ?? term).trim();
    if (!t) return;

    setErr("");
    setPartInfo(null);

    try {
      const qs = new URLSearchParams({ site, search_by: searchBy, term: t });
      const res = await apiFetch(`/api/receive/part?${qs}`);
      if (!res.ok) {
        const tx = await res.text().catch(() => "");
        throw new Error(tx || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setPartInfo(data);

      // Reset receive inputs like python
      setQtyReceived("");
      setQtyPerUom(String(data?.qty_per_unit_default ?? ""));
      setPo("(None)");

      // Reset advance inputs
      setShippingCost("");
      setShippingType("");
      setExpiration("");
    } catch (e) {
      console.error(e);
      setErr("Part not found.");
    }
  };

  const onScan = (text) => {
    const cleaned = (text || "").trim();
    setTerm(cleaned);
    setSearchBy("Part Number");
    loadPart(cleaned);
  };

  const saveReceive = async () => {
    if (!partInfo?.part) return;

    const pn = (partInfo.part["Part Number"] || "").trim();
    if (!pn) return;

    const qrN = parseFloat(qtyReceived || "0");
    const qpu = parseFloat(qtyPerUom || "0");
    if (!(qrN > 0) || !(qpu > 0)) {
      setErr("Enter Qty Received and Qty Per UOM (both > 0).");
      return;
    }

    const poRequired = !!partInfo.po_required;
    const poVal = po === "(None)" ? null : po;
    if (poRequired && !poVal) {
      setErr("PO is REQUIRED for this part. Please select a PO (Advance).");
      return;
    }

    setSaving(true);
    setErr("");

    try {
      const payload = {
        site,
        part_number: pn,
        qty_received: qrN,
        qty_per_uom: qpu,
        po_number: poVal,
        shipping_cost: shippingCost || null,
        shipping_type: shippingType || null,
        expiration: expiration || null,
      };

      const res = await apiFetch("/api/receive", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const tx = await res.text().catch(() => "");
        throw new Error(tx || `HTTP ${res.status}`);
      }

      const out = await res.json();

      alert(
        `Received OK.\nBatch: ${out.batch_id}\nNew total: ${out.new_total_quantity}\nUOM: ${out.uom_display}`
      );

      // ✅ ASK TO PRINT (ADD THIS RIGHT HERE)
      const ok = window.confirm("Print Barcode?\n\nDo you want to print a barcode label?");
      if (ok) {
        const p = partInfo.part;
        setPrintPart({
          part_number: pn,
          description: p?.Description || "",
          location: p?.Location || "",
          shelve: p?.Shelve || "",
          row: p?.Row || "",
          date: new Date().toISOString().slice(0, 10),
        });
        setPrintOpen(true);
      }

      // reset receive fields
      setQtyReceived("");
      setPo("(None)");
      setShippingCost("");
      setShippingType("");
      setExpiration("");

      // refresh part info
      await loadPart(pn);
    } catch (e) {
      console.error(e);
      setErr(`Receive failed: ${String(e.message || e)}`);
    } finally {
      setSaving(false);
    }
  };

  const p = partInfo?.part;

  // These are the labels you said you’re missing (Drum/Box/etc.)
  const packUom = (partInfo?.received_uom || "").trim(); // e.g. Box/Drum
  const unitUom = (partInfo?.unit_uom || "").trim();     // e.g. EA

  return (
    <div className="p-5">
      <QRScannerModal open={scanOpen} onClose={() => setScanOpen(false)} onResult={onScan} />

      {/* ✅ PRINT MODAL (ADD THIS HERE ONCE) */}
      <BarcodePrintModal
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        site={site}
        part={printPart}
      />

      <h2 className="text-2xl font-semibold mb-4">Receive Parts @ {site}</h2>

      {err && <p className="mb-3 text-red-600">{err}</p>}

      {/* Search */}
      <div className="border rounded-lg p-4 bg-white shadow-sm mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div>
            <label className="text-sm text-gray-600">Search By</label>
            <select
              className="border rounded px-2 py-2 w-full"
              value={searchBy}
              onChange={(e) => setSearchBy(e.target.value)}
            >
              <option>Part Number</option>
              <option>Manufacturer Part#</option>
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="text-sm text-gray-600">Search</label>
            <input
              className="border rounded px-2 py-2 w-full"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Enter part number or scan QR"
              onKeyDown={(e) => e.key === "Enter" && loadPart()}
            />
          </div>

          <div className="flex gap-2">
            <button
              className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              onClick={() => loadPart()}
              type="button"
            >
              Search
            </button>
            <button
              className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300"
              onClick={() => setScanOpen(true)}
              type="button"
            >
              Scan QR
            </button>
          </div>
        </div>
      </div>

      {/* Part details */}
      {p && (
        <div className="border rounded-lg p-4 bg-white shadow-sm mb-4">
          <div className="font-mono font-semibold">{p["Part Number"]}</div>
          <div className="text-gray-700">{p.Description}</div>

          <div className="text-sm text-gray-700 mt-2">
            <b>Total Qty:</b> {p["Total Quantity"]} &nbsp; | &nbsp;
            <b>UOM Type:</b> {partInfo.uom_display} &nbsp; | &nbsp;
            <b>Cost:</b> {partInfo.cost_display}
          </div>

          <div className="text-sm text-gray-700 mt-2">
            <b>Loc:</b> {p.Location || "-"} &nbsp;
            <b>Shelve:</b> {p.Shelve || "-"} &nbsp;
            <b>Row:</b> {p.Row || "-"}
          </div>

          <div className="mt-2 text-red-600 font-semibold text-sm">
            ⚠️ This part is being transacted as: {partInfo.uom_display}
            {partInfo.po_required ? "  |  PO REQUIRED" : ""}
          </div>
        </div>
      )}

      {/* Receive + Advance */}
      {p && (
        <div className="border rounded-lg p-4 bg-white shadow-sm">
          <h3 className="text-lg font-semibold mb-3">Receive Entry</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-gray-600">
                Qty Received{packUom ? ` (${packUom})` : ""}
              </label>
              <input
                className="border rounded px-2 py-2 w-full"
                value={qtyReceived}
                onChange={(e) => setQtyReceived(e.target.value)}
                inputMode="decimal"
                placeholder="e.g. 2"
              />
            </div>

            <div>
              <label className="text-sm text-gray-600">
                Qty Per UOM{unitUom ? ` (${unitUom} per ${packUom || "pack"})` : ""}
              </label>
              <input
                className="border rounded px-2 py-2 w-full"
                value={qtyPerUom}
                onChange={(e) => setQtyPerUom(e.target.value)}
                inputMode="decimal"
                placeholder="e.g. 10"
              />
            </div>
          </div>

          <details className="mt-4">
            <summary className="cursor-pointer font-semibold">Advance</summary>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
              <div>
                <label className="text-sm text-gray-600">
                  PO (select only){partInfo.po_required ? " *REQUIRED" : ""}
                </label>
                <select
                  className="border rounded px-2 py-2 w-full"
                  value={po}
                  onChange={(e) => setPo(e.target.value)}
                >
                  {poOptions.map((x) => (
                    <option key={x} value={x}>{x}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm text-gray-600">Shipping Cost</label>
                <input
                  className="border rounded px-2 py-2 w-full"
                  value={shippingCost}
                  onChange={(e) => setShippingCost(e.target.value)}
                  inputMode="decimal"
                  placeholder="e.g. 25.00"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Shipping Type</label>
                <select
                  className="border rounded px-2 py-2 w-full"
                  value={shippingType}
                  onChange={(e) => setShippingType(e.target.value)}
                >
                  {shippingTypes.map((x) => (
                    <option key={x} value={x === "(None)" ? "" : x}>{x}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm text-gray-600">Expiration</label>
                <input
                  type="date"
                  className="border rounded px-2 py-2 w-full"
                  value={expiration}
                  onChange={(e) => setExpiration(e.target.value)}
                />
              </div>
            </div>
          </details>

          <div className="mt-4">
            <button
              className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
              onClick={saveReceive}
              disabled={saving}
              type="button"
            >
              {saving ? "Saving…" : "Save Receive"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
